/* eslint-disable @typescript-eslint/prefer-optional-chain */
/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   LocalStorage Thunk
 */

import { initLocalStorage } from '@zcode-ui/web-zumper-global-state'
import type { RawLocalUser } from '@zcode/bizos'
import { STORAGE_PREFIX, localStorageSingleton } from '@zcode/nr-utils'
import type { FlaggedIds } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { receiveLocalizerValues } from './index'

const EQUAL_APPLY_MESSAGED = 'equalApplyMessaged'
const EQUAL_TOUR_MESSAGED = 'equalTourMessaged'
const EQUAL_CHECK_AVAILABILITY_MESSAGED = 'equalCheckAvailabilityMessaged'
const FAVORITES = 'favorites'
const FLAGGED_IDS = 'flaggedIds'
const MESSAGED = 'messaged'
const MESSAGING_USER = 'messagingUser'
const USER = 'user'
const USER_TOUR = 'userTour'
const VISITED = 'visited'

export function isLocalizerKey(prefixedKey: string | null) {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const key: string | undefined = prefixedKey?.split(`ls-${STORAGE_PREFIX}:`)[1]
  return (
    key === EQUAL_APPLY_MESSAGED ||
    key === EQUAL_TOUR_MESSAGED ||
    key === EQUAL_CHECK_AVAILABILITY_MESSAGED ||
    key === FAVORITES ||
    key === FLAGGED_IDS ||
    key === MESSAGED ||
    key === MESSAGING_USER ||
    key === USER ||
    key === USER_TOUR ||
    key === VISITED
  )
}

const getLocalizerFavorites = () => {
  const localFavorites = localStorageSingleton.get(FAVORITES)
  return localFavorites
}

export const getLocalizerFlagged = (): FlaggedIds => {
  const localFlagged = localStorageSingleton.get(FLAGGED_IDS) || {
    flaggedBuildings: {},
    flaggedListings: {},
  }
  return localFlagged
}

export const setLocalizerFlagged = (flaggedIds: FlaggedIds) => {
  localStorageSingleton.set(FLAGGED_IDS, flaggedIds)
}

export const getLocalizerMessaged = () => {
  const localMessaged = localStorageSingleton.get(MESSAGED)
  return localMessaged
}

export const getLocalizerEqualTourMessaged = () => {
  const localequalTourMessaged = localStorageSingleton.get(EQUAL_TOUR_MESSAGED)
  return localequalTourMessaged
}

export const getLocalizerEqualApplyMessaged = () => {
  const localequalApplyMessaged = localStorageSingleton.get(EQUAL_APPLY_MESSAGED)
  return localequalApplyMessaged
}

export const getLocalizerEqualCheckAvailabilityMessaged = () => {
  const localequalCheckAvailabilityMessaged = localStorageSingleton.get(
    EQUAL_CHECK_AVAILABILITY_MESSAGED
  )
  return localequalCheckAvailabilityMessaged
}

const getLocalizerVisited = () => {
  const localVisited = localStorageSingleton.get(VISITED)
  return localVisited
}

const getLocalizerUser = (): RawLocalUser | undefined => {
  // NOTE: nzr and djz have different structures for user information in local storage.
  // We will be leaving djz alone and make nzr backwards compatible.
  // In nzr, the local storage variable is 'user' and in djz, it is 'messagingUser'
  const localUser = localStorageSingleton.get(USER)
  const localMessagingUser = localStorageSingleton.getx(MESSAGING_USER)
  // NOTE: using `userTour` for backwards compatibilty as move-in date is stored separate in
  // `userTour` in djz
  const userTour = localStorageSingleton.getx(USER_TOUR)

  if (localUser) {
    // NOTE: in here, we assume that the the information we have is from nzr
    const localMoveInDate = localUser.tenant_profile && localUser.tenant_profile.move_in
    // remove move in date if not in the future
    if (localMoveInDate) {
      const moveInDate = new Date(localMoveInDate).getTime() <= Date.now() ? null : localMoveInDate
      if (localUser.tenant_profile) {
        localUser.tenant_profile.move_in = moveInDate
      } else {
        localUser.tenant_profile = { move_in: moveInDate }
      }
    }
    return localUser
  }
  if (localMessagingUser) {
    // NOTE: in here, we assume that the information we have is from djz. Therefore, we need to
    // transform the data into something nzr understands.
    const userData = JSON.parse(JSON.stringify(localMessagingUser))
    if (userData) {
      // NOTE: in djz, the name is simply stored as `name` compared to `first_name` + `last_name`
      // in nzr. Setting it to long_name will do as `fullName` setter of User bizo
      // will take care of the rest
      if (userData.name) {
        userData.long_name = userData.name
      }

      const moveInDate = userTour && userTour && userTour.moveIn
      if (moveInDate && new Date(moveInDate).getTime() >= Date.now()) {
        userData.tenant_profile = { move_in: moveInDate }
      }

      // also store it so that it can persists outside of our store on initialization
      localStorageSingleton.set(USER, userData)
      return userData
    }
  }
  return undefined
}

export const initLocalizer = () => (dispatch: Dispatch) => {
  const payload = {
    equalApplyMessaged: getLocalizerEqualApplyMessaged(),
    equalTourMessaged: getLocalizerEqualTourMessaged(),
    equalCheckAvailabilityMessaged: getLocalizerEqualCheckAvailabilityMessaged(),
    favorites: getLocalizerFavorites(),
    flaggedIds: getLocalizerFlagged(),
    messaged: getLocalizerMessaged(),
    visited: getLocalizerVisited(),
    user: getLocalizerUser(),
  }

  dispatch(receiveLocalizerValues(payload))
  dispatch(initLocalStorage())
}
